import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import ComprehensiveFooter from '../ComprehensiveFooter';

const UseCasesLanding = () => {
  const useCaseCategories = [
    {
      id: 'energy',
      title: 'Energy: Oil & Gas Exploration',
      description: 'Quantum-accelerated seismic inversion and geological exploration',
      image: '/images/energy-quantum.jpg',
      caseStudyUrl: 'https://dyota.substack.com/p/applied-quantum-computing-case-study',
      keyStats: ['90% Cost Reduction', '18h → 2.5h Processing', '8.3% Higher Hit Rate'],
      route: '/use-cases/energy'
    },
    {
      id: 'finance',
      title: 'Finance & Trading',
      description: 'High-frequency trading with quantum-enhanced volatility prediction',
      image: '/images/finance-quantum.jpg',
      caseStudyUrl: 'https://dyota.substack.com/p/applied-quantum-computing-in-finance',
      keyStats: ['Sharpe +0.42', '+3.7% Daily PnL', '6.3ms Inference'],
      route: '/use-cases/finance'
    },
    {
      id: 'ai',
      title: 'AI Infrastructure',
      description: 'Quantum sidecars achieving unprecedented alpha generation',
      image: '/images/ai-quantum.jpg',
      caseStudyUrl: 'https://dyota.substack.com/p/case-study-ai-realm-infrastructure',
      keyStats: ['$220M Annual Alpha', '19% Lower Drawdowns', '99.9998% Uptime'],
      route: '/use-cases/ai'
    },
    {
      id: 'healthcare',
      title: 'Healthcare',
      description: 'Quantum drug discovery and molecular simulation',
      image: '/images/healthcare-quantum.jpg',
      caseStudyUrl: '#healthcare-case-study',
      keyStats: ['70% Faster Discovery', '99.7% Accuracy', '60% Cost Reduction'],
      route: '/use-cases/healthcare'
    },
    {
      id: 'research',
      title: 'Research',
      description: 'Academic and scientific research acceleration',
      image: '/images/research-quantum.jpg',
      caseStudyUrl: '#research-case-study',
      keyStats: ['5× Research Speed', '1000× Problem Scale', '99.9% Accuracy'],
      route: '/use-cases/research'
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="py-24 bg-black relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(0,255,209,0.1),transparent_50%)]"></div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <Badge className="mb-6 bg-emerald-500/10 text-emerald-300 border-emerald-500/20">
              Real-World Impact
            </Badge>
            <h1 className="display-large mb-6 text-white">
              Quantum Computing Use Cases
            </h1>
            <p className="body-large text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Explore how Godquantum Systems is transforming industries through practical quantum computing solutions. 
              From breakthrough research to billion-dollar trading strategies, discover the quantum advantage in action.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Use Cases Grid */}
      <section className="py-16 bg-black">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {useCaseCategories.map((useCase, index) => (
              <motion.div
                key={useCase.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-6 h-full border-white/10 bg-black/40 hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-all duration-300 group">
                  <div className="mb-4">
                    <div className="w-full h-48 bg-gradient-to-br from-emerald-500/20 to-blue-500/20 rounded-lg mb-4 flex items-center justify-center">
                      <span className="text-white/60 text-sm">Quantum Visualization</span>
                    </div>
                    <h3 className="heading-2 mb-3 text-white group-hover:text-emerald-300 transition-colors">
                      {useCase.title}
                    </h3>
                    <p className="text-gray-300 text-sm mb-4 leading-relaxed">
                      {useCase.description}
                    </p>
                  </div>

                  {/* Key Stats */}
                  <div className="mb-6">
                    <h4 className="text-white font-semibold mb-3 text-sm">Key Results:</h4>
                    <div className="space-y-2">
                      {useCase.keyStats.map((stat, statIndex) => (
                        <div key={statIndex} className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                          <span className="text-emerald-300 text-sm font-medium">{stat}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-3 mt-auto">
                    <Button 
                      className="btn-primary w-full"
                      onClick={() => window.open(useCase.caseStudyUrl, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Read Case Study
                    </Button>
                    <Button 
                      className="btn-secondary w-full"
                      onClick={() => window.location.href = useCase.route}
                    >
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-emerald-900/20 to-blue-900/20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="heading-2 mb-4 text-white">Don't See Your Industry?</h2>
            <p className="body-large text-gray-300 mb-8">
              Quantum computing applications span across every industry. Let's discuss how we can solve your specific challenges.
            </p>
            <Button className="btn-primary px-8 py-4">
              Schedule a Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      <ComprehensiveFooter />
    </div>
  );
};

export default UseCasesLanding;